import { Octokit } from "@octokit/rest";
import { Endpoints, OctokitResponse } from "@octokit/types";
import store, { UserSearchFilters } from "../store";

export type Users = Endpoints["GET /search/users"]["response"]["data"]["items"];
export type UserDetails = Endpoints["GET /users/{username}"]["response"]["data"];

export type PaginatedUsers = {
  users: Users;
  pageCount: number;
  resultsPerPage: number;
  totalCount: number;
};

export type UsersResponse = OctokitResponse<Users>;

export const RESULTS_PER_PAGE = 20;
const GITHUB_RESULTS_PER_PAGE = 100;

type CachedUsersResponse = {
  users: Users;
  totalCount: number;
};

const usersResponseCache = new Map<string, CachedUsersResponse>();

const emptyFilters: Readonly<UserSearchFilters> = {
  language: "",
  minFollowers: "",
  minRepos: "",
  accountType: "",
  sort: "",
};

const buildUsersQuery = (location: string, filters: Readonly<UserSearchFilters>) =>
  [
    `location:${location}`,
    filters.language ? `language:${filters.language.trim()}` : "",
    filters.minFollowers ? `followers:>=${filters.minFollowers}` : "",
    filters.minRepos ? `repos:>=${filters.minRepos}` : "",
    filters.accountType ? `type:${filters.accountType}` : "",
  ]
    .filter(Boolean)
    .join(" ");

const getGithubRateLimitMessage = (status?: number) =>
  status === 403 || status === 429
    ? "GitHub's rate limit was reached. Please try again in a few minutes."
    : "";

const getFunctionJson = async <ResponseBody>(url: string) => {
  const response = await fetch(url);
  const contentType = response.headers.get("content-type") || "";

  if (!contentType.includes("application/json")) {
    throw new Error("Netlify function is not available.");
  }

  const data = await response.json();

  if (!response.ok) {
    const error = new Error(data.message || "Request failed.") as Error & {
      status?: number;
    };
    error.status = response.status;
    throw error;
  }

  return data as ResponseBody;
};

const getSearchUsersFunctionUrl = (
  location: string,
  githubPage: number,
  filters: Readonly<UserSearchFilters>
) => {
  const params = new URLSearchParams({
    location,
    page: String(githubPage),
  });

  if (filters.language) {
    params.set("language", filters.language.trim());
  }

  if (filters.minFollowers) {
    params.set("minFollowers", filters.minFollowers);
  }

  if (filters.minRepos) {
    params.set("minRepos", filters.minRepos);
  }

  if (filters.accountType) {
    params.set("accountType", filters.accountType);
  }

  if (filters.sort) {
    params.set("sort", filters.sort);
  }

  return `/.netlify/functions/search-users?${params.toString()}`;
};

export const userSearch = async (
  location: string,
  page = 1,
  resultsPerPage = RESULTS_PER_PAGE,
  filters: Readonly<UserSearchFilters> = emptyFilters
) => {
  try {
    store.error = "";
    const octokit = new Octokit();

    if (!location) {
      return;
    }

    const normalizedLocation = location.trim().toLowerCase();
    const githubPage = Math.ceil(
      (page * resultsPerPage) / GITHUB_RESULTS_PER_PAGE
    );
    const cacheKey = [
      normalizedLocation,
      filters.language.trim().toLowerCase(),
      filters.minFollowers,
      filters.minRepos,
      filters.accountType,
      filters.sort,
      githubPage,
    ].join(":");
    let usersResponse = usersResponseCache.get(cacheKey);

    if (!usersResponse) {
      let fetchedResponse: CachedUsersResponse;

      try {
        fetchedResponse = await getFunctionJson<CachedUsersResponse>(
          getSearchUsersFunctionUrl(location.trim(), githubPage, filters)
        );
      } catch (functionError: any) {
        if (functionError.status) {
          throw functionError;
        }

        const query = buildUsersQuery(location.trim(), filters);
        const searchOptions: Endpoints["GET /search/users"]["parameters"] = {
          q: query,
          per_page: GITHUB_RESULTS_PER_PAGE,
          page: githubPage,
        };

        if (filters.sort) {
          searchOptions.sort = filters.sort;
          searchOptions.order = "desc";
        }

        fetchedResponse = await octokit.search.users(searchOptions).then((response) => ({
          users: response.data.items as Users,
          totalCount: response.data.total_count,
        }));
      }

      usersResponseCache.set(cacheKey, fetchedResponse);
      usersResponse = fetchedResponse;
    }

    const pageCount = Math.ceil(
      (usersResponse.totalCount > 1000 ? 1000 : usersResponse.totalCount) /
        RESULTS_PER_PAGE
    );

    const firstResultIndex =
      ((page - 1) * resultsPerPage) % GITHUB_RESULTS_PER_PAGE;
    const users = usersResponse.users.slice(
      firstResultIndex,
      firstResultIndex + resultsPerPage
    ) as Users;

    store.users = users;
    store.totalUsersCount = usersResponse.totalCount;
    store.pageCount = pageCount;
  } catch (error: any) {
    if (error) {
      store.error =
        error.status === 403 || error.status === 429
          ? "GitHub's search rate limit was reached. Please try again in a few minutes."
          : error.message;
      return;
    }
  }
};

export const fetchUserDetails = async (username: string) => {
  if (store.userDetails[username] || store.loadingUserDetails[username]) {
    return;
  }

  try {
    const octokit = new Octokit();
    store.loadingUserDetails[username] = true;
    store.userDetailsError[username] = "";

    try {
      store.userDetails[username] = await getFunctionJson<UserDetails>(
        `/.netlify/functions/user-details?username=${encodeURIComponent(
          username
        )}`
      );
    } catch (functionError: any) {
      if (functionError.status) {
        throw functionError;
      }

      const response = await octokit.users.getByUsername({ username });
      store.userDetails[username] = response.data;
    }
  } catch (error: any) {
    if (error) {
      store.userDetailsError[username] =
        getGithubRateLimitMessage(error.status) || error.message;
    }
  } finally {
    store.loadingUserDetails[username] = false;
  }
};
